import React, { useState } from 'react';
import { PaymentForm, CreditCard, GooglePay } from 'react-square-web-payments-sdk';
import { Loader2, CreditCard as CardIcon, Lock, Apple, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../utils/api';

// Square Application IDs
const SQUARE_APP_ID = process.env.REACT_APP_SQUARE_APP_ID || 'sq0idp-RAcpI-KE3j4SU0smO5Zn8g';
const SQUARE_LOCATION_ID = process.env.REACT_APP_SQUARE_LOCATION_ID || 'LQQ9GGWKR8766';

// Log Square configuration on load (for debugging)
console.log('[Square Config] App ID:', SQUARE_APP_ID);
console.log('[Square Config] Location ID:', SQUARE_LOCATION_ID);
console.log('[Square Config] Environment:', SQUARE_APP_ID.includes('sandbox') ? 'SANDBOX' : 'PRODUCTION');

// Safari-safe POST request using XMLHttpRequest
// This avoids the "body is disturbed or locked" error on iOS Safari
function safariSafePost(url, body, customHeaders = {}) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    
    // Add custom headers (e.g., X-Booking-Token)
    Object.entries(customHeaders).forEach(([key, value]) => {
      xhr.setRequestHeader(key, value);
    });
    
    xhr.onload = function() {
      try {
        const data = xhr.responseText ? JSON.parse(xhr.responseText) : {};
        resolve({
          ok: xhr.status >= 200 && xhr.status < 300,
          status: xhr.status,
          data
        });
      } catch (e) {
        console.error('[Payment] XHR parse error:', e);
        reject(new Error('Failed to parse response'));
      }
    };
    
    xhr.onerror = function() {
      console.error('[Payment] XHR network error');
      reject(new Error('Network error'));
    };
    
    xhr.ontimeout = function() {
      console.error('[Payment] XHR timeout');
      reject(new Error('Request timeout'));
    };
    
    xhr.timeout = 30000; // 30 second timeout
    xhr.send(JSON.stringify(body));
  });
}

export default function SquarePaymentForm({ 
  amount, 
  currency = 'GBP',
  bookingId,
  paymentType = 'deposit',
  onPaymentSuccess,
  onPaymentError,
  disabled = false,
  customPaymentEndpoint = null,
  customPaymentBody = {},
  customHeaders = {},  // NEW: Custom headers for booking token auth
  storeCardMode = false,
  customPaymentHandler = null,
  title = null,
  subtitle = null,
  buttonText = null,
  customerName = null,
  customerEmail = null
}) {
  const [processing, setProcessing] = useState(false);
  const [errorDetails, setErrorDetails] = useState(null);

  const handlePayment = async (token, buyer) => {
    console.log('[Payment] Token received:', typeof token, token);
    
    // Helper to extract error message from any error format
    const extractErrorMessage = (err) => {
      if (!err) return 'Unknown error';
      if (typeof err === 'string') return err;
      if (err.message) return err.message;
      if (err.detail) return err.detail;
      if (err.type) return err.type;
      if (err.code) return err.code;
      // Try to stringify if nothing else works
      try {
        const str = JSON.stringify(err);
        if (str !== '{}') return str;
      } catch (e) {}
      return 'Payment error';
    };
    
    // Check if token is actually an array of errors
    if (Array.isArray(token)) {
      console.error('[Payment] Received error array:', JSON.stringify(token, null, 2));
      const errorMessages = token.map(extractErrorMessage).join(', ');
      toast.error(errorMessages || 'Card verification failed');
      onPaymentError?.(errorMessages);
      return;
    }
    
    // Check if the response contains errors property (Square SDK pattern)
    if (token?.errors && Array.isArray(token.errors) && token.errors.length > 0) {
      console.error('[Payment] Square returned errors:', JSON.stringify(token.errors, null, 2));
      const errorMessages = token.errors.map(extractErrorMessage).join(', ');
      toast.error(errorMessages || 'Card verification failed');
      onPaymentError?.(errorMessages);
      return;
    }
    
    // Check if token itself is an error object (not a token string)
    if (token && typeof token === 'object' && !token.token && (token.message || token.error || token.detail)) {
      console.error('[Payment] Token is error object:', JSON.stringify(token, null, 2));
      const errorMsg = extractErrorMessage(token);
      toast.error(errorMsg);
      onPaymentError?.(errorMsg);
      return;
    }
    
    if (!token?.token) {
      console.error('[Payment] No token received from Square:', token);
      toast.error('Payment failed - no token received');
      onPaymentError?.('No payment token received');
      return;
    }

    // Prevent double-processing
    if (processing) {
      console.log('[Payment] Already processing, ignoring duplicate call');
      return;
    }

    setProcessing(true);
    setErrorDetails(null);
    
    // If in store card mode and custom handler provided, use it
    if (storeCardMode && customPaymentHandler) {
      try {
        console.log('[Payment] Store card mode - using custom handler');
        const result = await customPaymentHandler(token.token, buyer?.token);
        if (result.success) {
          onPaymentSuccess?.({ payment_token: token.token, verification_token: buyer?.token, ...result });
        } else {
          throw new Error(result.error || 'Failed to store card');
        }
      } catch (error) {
        console.error('[Payment] Store card error:', error);
        toast.error(error.message || 'Failed to save card');
        onPaymentError?.(error.message);
      } finally {
        setProcessing(false);
      }
      return;
    }
    
    // Use custom endpoint if provided, otherwise default
    const paymentUrl = customPaymentEndpoint || `${API_URL}/api/weekends/bookings/${bookingId}/payment`;
    
    console.log('[Payment] Sending to:', paymentUrl);
    console.log('[Payment] Store card mode:', storeCardMode);
    console.log('[Payment] Token:', token.token?.substring(0, 20) + '...');
    console.log('[Payment] Verification token:', buyer?.token ? 'present' : 'missing');
    
    // Build request body
    const requestBody = customPaymentEndpoint 
      ? {
          ...customPaymentBody,
          source_id: token.token,
          verification_token: buyer?.token
          // Note: don't include 'amount' as shop checkout calculates it from cart
        }
      : {
          source_id: token.token,
          payment_type: paymentType,
          verification_token: buyer?.token,
          store_card: storeCardMode  // Request card storage for payment plans
        };
    
    console.log('[Payment] Request body:', JSON.stringify(requestBody, null, 2));
    
    try {
      console.log('[Payment] Making XHR request...');
      
      // Use XMLHttpRequest to avoid Safari "body disturbed" issue
      // Pass customHeaders for booking token auth
      const response = await safariSafePost(paymentUrl, requestBody, customHeaders);
      
      console.log('[Payment] Response received:', response.status, response.ok);
      
      // Handle 401 - token expired
      if (response.status === 401) {
        console.error('[Payment] Authentication failed - token may be expired');
        throw new Error(response.data?.detail || 'Booking session expired. Please verify your booking again.');
      }

      if (response.ok && response.data.success) {
        console.log('[Payment] Success!');
        toast.success('Payment successful!');
        onPaymentSuccess?.(response.data);
      } else {
        // Handle error - detail can be string or array (Pydantic validation errors)
        let errorMsg = response.data.detail || response.data.message || 'Payment failed';
        
        // If detail is an array (Pydantic validation errors), extract messages
        if (Array.isArray(errorMsg)) {
          errorMsg = errorMsg.map(e => e.msg || e.message || e.detail || 'Validation error').join(', ');
        }
        
        console.error('[Payment] Failed:', errorMsg);
        throw new Error(errorMsg);
      }
    } catch (error) {
      console.error('[Payment] Error:', error.message, error);
      
      // Extract readable error message
      let errorMessage = error.message || String(error);
      
      // Handle [object Object] case (shouldn't happen now but just in case)
      if (errorMessage.includes('[object Object]')) {
        errorMessage = 'Payment processing failed. Please try again.';
      }
      
      // Check if it's the Safari "body disturbed" error
      if (errorMessage.toLowerCase().includes('body') && errorMessage.toLowerCase().includes('disturbed')) {
        setErrorDetails('Safari compatibility issue detected. Please try using Chrome or Firefox, or contact support.');
        toast.error('Browser compatibility issue. Please try a different browser.');
      } else {
        toast.error(errorMessage || 'Payment processing failed');
      }
      onPaymentError?.(errorMessage);
    } finally {
      setProcessing(false);
    }
  };

  // Handle errors from Square SDK tokenization
  const handleTokenizeError = (errors) => {
    console.error('[Payment] Square tokenization errors:', JSON.stringify(errors, null, 2));
    if (errors && errors.length > 0) {
      // Square SDK errors can have different structures - extract message safely
      const errorMessages = errors.map(e => {
        if (typeof e === 'string') return e;
        if (e.message) return e.message;
        if (e.type) return e.type;
        if (e.field) return `${e.field}: ${e.type || 'invalid'}`;
        return 'Card validation error';
      }).join(', ');
      toast.error(`Card error: ${errorMessages}`);
      setErrorDetails(errorMessages);
    }
  };

  return (
    <div className="space-y-4">
      {title !== '' && (
        <div className="flex items-center gap-2 text-sm text-slate-400 mb-4">
          <Lock className="w-4 h-4" />
          <span>{title || 'Secure payment powered by Square'}</span>
        </div>
      )}
      {subtitle && (
        <p className="text-slate-400 text-xs mb-2">{subtitle}</p>
      )}

      <PaymentForm
        applicationId={SQUARE_APP_ID}
        locationId={SQUARE_LOCATION_ID}
        cardTokenizeResponseReceived={handlePayment}
        createPaymentRequest={() => ({
          countryCode: 'GB',
          currencyCode: currency,
          total: {
            amount: String(amount / 100), // Convert pence to pounds for display
            label: 'Line Dance Central',
          },
          requestBillingContact: true,
          requestShippingContact: false,
        })}
        // For STORE mode, we don't need verification details (Cards API handles differently)
        // For CHARGE mode, we need full SCA verification
        createVerificationDetails={storeCardMode ? undefined : () => ({
          amount: String(amount / 100),
          currencyCode: currency,
          intent: 'CHARGE',
          billingContact: {
            countryCode: 'GB',
          },
        })}
        overrides={{
          scriptSrc: 'https://web.squarecdn.com/v1/square.js'
        }}
      >
        {/* Google Pay - only shows on Chrome/Android if supported, hide in store card mode */}
        {!storeCardMode && <GooglePay />}
        
        {/* Divider - only show if Google Pay is visible */}
        {!storeCardMode && (
          <div className="flex items-center gap-3 my-4">
            <div className="flex-1 h-px bg-slate-700"></div>
            <span className="text-slate-400 text-sm">or pay with card</span>
            <div className="flex-1 h-px bg-slate-700"></div>
          </div>
        )}
        
        {/* Custom labels for UK terminology */}
        <div className="text-xs text-slate-400 mb-2">
          <p>Card Number • Expiry (MM/YY) • CVV • Post Code</p>
        </div>
        
        <CreditCard 
          buttonProps={{
            isLoading: processing || disabled,
          }}
          includeInputLabels
          style={{
            input: {
              backgroundColor: '#1e293b',
              color: '#f1f5f9',
              fontFamily: 'inherit',
            },
            'input::placeholder': {
              color: '#64748b',
            },
            '.input-container': {
              borderColor: '#334155',
              borderRadius: '6px',
            },
            '.input-container.is-focus': {
              borderColor: '#7c3aed',
            },
            '.input-container.is-error': {
              borderColor: '#ef4444',
            },
            '.message-text': {
              color: '#94a3b8',
            },
            '.message-text.is-error': {
              color: '#ef4444',
            },
          }}
        >
          {processing ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin inline" />
              {storeCardMode ? 'Saving...' : 'Processing...'}
            </>
          ) : (
            <>
              <CardIcon className="w-4 h-4 mr-2 inline" />
              {buttonText || (storeCardMode ? 'Save Card' : `Pay £${(amount / 100).toFixed(2)}`)}
            </>
          )}
        </CreditCard>
      </PaymentForm>

      {/* Error details display */}
      {errorDetails && (
        <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
          {errorDetails}
        </div>
      )}

      {/* Test card info for sandbox */}
      {SQUARE_APP_ID.includes('sandbox') && (
        <div className="text-xs text-slate-400 text-center space-y-1 pt-2">
          <p className="font-medium text-slate-400">Test Card Numbers:</p>
          <p>Success: 4532 0123 4567 8901</p>
          <p>CVV: Any 3 digits | Exp: Any future date | Post Code: Any 5 characters</p>
        </div>
      )}
    </div>
  );
}
